import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, Linkedin, MapPin } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/App";
import { Reveal, Eyebrow } from "@/components/Reveal";

export default function Contact() {
  const { settings } = useSettings();
  const c = settings?.contact || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const inputCls = "w-full border-b border-gold/25 bg-transparent py-2.5 font-sans text-sm text-warmwhite outline-none placeholder:text-softgrey/40 focus:border-gold";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/enquiries", { ...form, opportunity_title: "General Contact" });
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-obsidian px-6 pt-32 pb-24 lg:px-10" data-testid="contact-page">
      <div className="mx-auto grid max-w-5xl gap-16 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>Contact</Eyebrow>
          <h1 className="mt-5 font-serif text-4xl leading-tight text-warmwhite lg:text-5xl">Discreet enquiries welcome.</h1>
          <p className="mt-5 font-sans text-sm text-softgrey">Reach out or submit your requirements — we respond confidentially.</p>
          <div className="mt-10 space-y-5">
            <a href={`mailto:${c.email || ""}`} className="flex items-center gap-4 font-sans text-sm text-softgrey hover:text-warmwhite"><Mail size={17} className="text-gold" /> {c.email || "contact@goldstonesourcing.com"}</a>
            {c.phone && <a href={`tel:${(c.phone || "").replace(/\s+/g, "")}`} className="flex items-center gap-4 font-sans text-sm text-softgrey hover:text-warmwhite"><Phone size={17} className="text-gold" /> {c.phone}</a>}
            {c.whatsapp && <a href={`https://wa.me/${(c.whatsapp || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 font-sans text-sm text-softgrey hover:text-warmwhite"><MessageCircle size={17} className="text-gold" /> {c.whatsapp}</a>}
            {c.linkedin && <a href={c.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-4 font-sans text-sm text-softgrey hover:text-warmwhite"><Linkedin size={17} className="text-gold" /> LinkedIn</a>}
            {c.address && <div className="flex items-start gap-4 font-sans text-sm text-softgrey"><MapPin size={17} className="mt-0.5 shrink-0 text-gold" /> <span>{c.address}</span></div>}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="space-y-5 border border-gold/15 bg-seablue/15 p-8">
            <input required placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="contact-name" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="contact-email" />
            <input placeholder="Phone / WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="contact-phone" />
            <textarea placeholder="Message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} data-testid="contact-message" />
            <button type="submit" disabled={submitting} className="w-full bg-gold py-3.5 font-sans text-sm font-medium text-obsidian transition-all hover:brightness-110 disabled:opacity-60" data-testid="contact-submit">
              {submitting ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
